import { getData } from 'services/api';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IdParamsType } from 'utils/types';
import { camelizeKeys } from 'humps';

/**
 * One scanned ticket from `GET /tickets/history/:event_id`. The backend already
 * filters to validated tickets (`validated = 1 OR validated_times > 0`) and
 * excludes refunded/comp-canceled transactions, so every row is a real entry.
 * `scannedAt` is an ISO string (camelizeKeys converts the key, not the value)
 * or null. This is the only per-ticket scan timestamp the API exposes — the
 * /scans endpoint only keeps MAX(scanned_at) per scanner.
 */
export interface ScanHistoryTicket {
	ticket: {
		id: number;
		ticketTypeId: number;
		validated: boolean;
		validatedTimes: number;
		scannedAt: string | null;
		lastTransaction: number;
		userId: number;
		seatId?: string;
	};
	email: string;
	fullName: string;
	ticketTypeName: string;
	ticketTypeColor: string | null;
}

/**
 * Per-ticket-type scanned-vs-sold aggregate from the same `GET /tickets/history` payload (Go
 * `TicketTypeStats`, a real per-type SQL aggregate). Drives the Scan-Tickets attendance donuts — `sold` is the
 * ring, `scanned` is the filled arc, and the gap is who is still expected.
 */
export interface TicketTypeStat {
	ticketTypeId: number;
	ticketTypeName: string;
	soldTickets: number;
	scannedTickets: number;
}

export interface ScanHistory {
	soldTickets: number;
	scannedTickets: number;
	totalSold: number;
	totalScanned: number;
	tickets: ScanHistoryTicket[];
	/** Per-type breakdown; absent on older payloads, so always guard before use. */
	ticketTypeStats?: TicketTypeStat[];
}

/**
 * @param refetchInterval Poll interval in ms while mounted (e.g. 60_000 for the
 *   Scanned Tickets auto-refresh toggle). Omit/undefined disables polling — the
 *   default, since there is no live push channel.
 */
export const useGetScanHistory = (refetchInterval?: number) => {
	const { id: eventId } = useParams<IdParamsType>();

	const { data, isLoading, refetch, isFetching, isError } = useQuery({
		queryKey: ['scan-history', eventId],
		queryFn: (): Promise<ScanHistory> => getData(`/tickets/history/${eventId}`),
		enabled: !!eventId,
		select: (raw) => camelizeKeys(raw) as ScanHistory,
		refetchInterval,
		// Keep polling while the tab is backgrounded — the door monitor often sits on an unfocused
		// second screen, and the manual setInterval this replaced was focus-agnostic too.
		refetchIntervalInBackground: true,
	});

	return { data, isLoading, refetch, isFetching, isError };
};
